import { handleApiResult } from '@store/error';
import { apiSlice } from './apiSlice';

export interface License {
  licenseId: string;
  /** Activation id — absent until the administrator assigns one. */
  id?: string;
  label?: string;
  deviceLimitCount: number;
  composerDeviceLimitCount: number;
  applicationFeatures: string[];
  featureDetail?: Record<string, unknown>;
  issueDate: string;
  expirationDate: string;
  seatAllocationTimeInSeconds: number;
  sourceFileId: string;
  /** False until the administrator sets a password; the license serves no seats. */
  passwordSet: boolean;
  locked: boolean;
  isFloating: boolean;
  /** Seats still held, counted per application family. */
  usedDeviceCount: number;
  usedComposerDeviceCount: number;
  /**
   * Seat rows that have gone quiet past every window. They are free for the next client that
   * asks, so they are not "in use", but nothing sweeps them in the background either, which is
   * why they are reported rather than simply ignored.
   */
  reclaimableDeviceCount: number;
  reclaimableComposerDeviceCount: number;
  /** Whole days until `expirationDate` on the server's clock; zero or less once it has passed. */
  expiresInDays: number;
  /** 60, 30, 14 or 7 from 60 days before expiry: the reminder point most recently crossed. */
  expiryReminderDays?: number;
  createdAt: string;
  updatedAt: string;
}

export const licenseApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getLicenses: builder.query<License[], void>({
      query: () => '/v1/licenses',
      providesTags: ['License'],
      onQueryStarted: handleApiResult(),
    }),
    // Seat counts and features are entitlements and are not editable here by design.
    updateLicense: builder.mutation<License, { licenseId: string, label?: string, locked?: boolean }>({
      query: ({ licenseId, ...body }) => ({ url: `/v1/licenses/${licenseId}`, method: 'PATCH', body }),
      invalidatesTags: ['License'],
      onQueryStarted: handleApiResult('License updated.'),
    }),
    setActivationId: builder.mutation<License, { licenseId: string, id: string }>({
      query: ({ licenseId, id }) => ({
        url: `/v1/licenses/${licenseId}/activation-id`, method: 'PUT', body: { id }
      }),
      invalidatesTags: ['License', 'Installation'],
      onQueryStarted: handleApiResult('Activation id set.'),
    }),
    setLicensePassword: builder.mutation<void, { licenseId: string, password: string }>({
      query: ({ licenseId, password }) => ({
        url: `/v1/licenses/${licenseId}/password`, method: 'PUT', body: { password }
      }),
      invalidatesTags: ['License', 'Installation'],
      onQueryStarted: handleApiResult('License password set.'),
    }),
  }),
});

export const {
  useGetLicensesQuery,
  useUpdateLicenseMutation,
  useSetActivationIdMutation,
  useSetLicensePasswordMutation,
} = licenseApiSlice;
